import React from 'react';
import { AIWorkflowChat } from '@/components/workflow/AIWorkflowChat';
import { workflowNodeGenerator } from '@/services/workflow/WorkflowNodeGenerator';
import { WorkflowSuggestion } from '@/stores/workflow/workflow-chat-store';
import { toast } from 'sonner';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { WorkflowEdgeSnapshot, WorkflowNodeSnapshot } from '@/types/workflow-editor';

export const WorkflowAIChatTab: React.FC = () => {
  const {
    workflowId,
    workflow,
    nodes,
    edges,
    setNodes,
    setEdges,
    selectedNode,
    executionSteps,
    executionLogs,
    effectiveExecutionKey,
    serverDebugMode,
  } = useWorkflowEditor();

  return (
    <AIWorkflowChat
      workflowId={workflowId || undefined}
      workflowName={workflow?.name}
      currentNodes={nodes.map<WorkflowNodeSnapshot>((n) => ({
        id: n.id,
        type: n.type || '',
        data: n.data,
        position: n.position,
        config: n.data.config,
      }))}
      currentEdges={edges.map<WorkflowEdgeSnapshot>((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
      }))}
      executionSteps={executionSteps}
      executionLogs={executionLogs}
      activeExecutionKey={effectiveExecutionKey}
      executionSource={serverDebugMode ? 'server' : 'esp32'}
      onAcceptSuggestion={(suggestion: WorkflowSuggestion) => {
        let currentNodes = nodes;
        let currentEdges = edges;
        const changeMessages: string[] = [];

        if (suggestion.nodeModifications && suggestion.nodeModifications.length > 0) {
          const modResult = workflowNodeGenerator.applyNodeModifications(
            currentNodes,
            suggestion.nodeModifications
          );
          currentNodes = modResult.nodes;
          changeMessages.push(...modResult.changesSummary);
        }

        if (suggestion.nodes.length > 0) {
          const result = workflowNodeGenerator.generateFromSuggestion(
            suggestion,
            {
              existingNodes: currentNodes,
              existingEdges: currentEdges,
              autoLayout: true,
              connectToNodeId: selectedNode?.id,
              positionHint: 'after',
            }
          );
          currentNodes = result.allNodes;
          currentEdges = result.allEdges;
          if (result.nodes.length > 0) {
            changeMessages.push(`Added ${result.nodes.length} node(s)`);
          }
        } else if (suggestion.edgesToRemove && suggestion.edgesToRemove.length > 0) {
          const edgesToRemoveSet = new Set(suggestion.edgesToRemove);
          currentEdges = currentEdges.filter(e => !edgesToRemoveSet.has(e.id));
        }

        setNodes(currentNodes);
        setEdges(currentEdges);

        const modCount = suggestion.nodeModifications?.length || 0;
        const nodeCount = suggestion.nodes.length;
        const totalChanges = modCount + nodeCount;

        if (totalChanges > 0) {
          let message = '';
          if (modCount > 0 && nodeCount > 0) {
            message = `Modified ${modCount} node(s) and added ${nodeCount} node(s)`;
          } else if (modCount > 0) {
            message = `Modified ${modCount} node(s)`;
          } else {
            message = `Added ${nodeCount} node(s) to workflow`;
          }
          toast.success(message, { description: suggestion.description });
        }
      }}
      onAddNodes={(suggestedNodes) => {
        const tempSuggestion: WorkflowSuggestion = {
          description: `Adding ${suggestedNodes.length} node(s)`,
          nodes: suggestedNodes,
          isComplete: true,
        };
        const result = workflowNodeGenerator.generateFromSuggestion(
          tempSuggestion,
          {
            existingNodes: nodes,
            existingEdges: edges,
            autoLayout: true,
            connectToNodeId: selectedNode?.id,
            positionHint: 'after',
          }
        );
        setNodes(result.allNodes);
        setEdges(result.allEdges);
        toast.success(`Added ${result.nodes.length} node(s) to workflow`);
      }}
      className="flex-1"
    />
  );
};
